import {
  uuid,
  index,
  pgTable,
  foreignKey,
  unique,
} from 'drizzle-orm/pg-core';
import { defaultFields, defaultHistoryFields } from '@trashlab/api/drizzle/default';
import { driver } from '@trashlab/api/src/domains/driver/schemas/driver';
import { business_line_config } from '@trashlab/api/src/domains/business-lines/schemas/business-line';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';

/**
 * Junction table for many-to-many relationship between drivers and business lines.
 * A driver can be assigned to multiple business lines, and a business line can have multiple drivers.
 * This enables filtering the dispatch board by business line(s).
 */
export const driver_business_line = pgTable(
  'driver_business_line',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** The driver being assigned to a business line */
    driverId: uuid('driverId').notNull(),
    /** The business line the driver is assigned to */
    businessLineId: uuid('businessLineId').notNull(),
    /** User who created this assignment */
    createdBy: uuid('createdBy'),
    /** User who last modified this assignment */
    lastModifiedBy: uuid('lastModifiedBy'),
  },
  (table) => {
    return {
      // Standard indexes
      createdAtTs_idx: index('driver_business_line_createdAtTs_idx').on(table.createdAtTs),
      tenantId_idx: index('driver_business_line_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('driver_business_line_updatedAtTs_idx').on(table.updatedAtTs),
      updatedAtTs_idx_desc: index('driver_business_line_updatedAtTs_idx_desc').on(table.updatedAtTs.desc()),
      versionNumber_idx: index('driver_business_line_versionNumber_idx').on(table.versionNumber),

      // Lookup indexes
      driverId_idx: index('driver_business_line_driverId_idx').on(table.driverId),
      businessLineId_idx: index('driver_business_line_businessLineId_idx').on(table.businessLineId),

      // Composite index for efficient lookups
      tenant_driver_idx: index('driver_business_line_tenant_driver_idx').on(
        table.tenantId,
        table.driverId,
      ),

      // Unique constraint: a driver can only be assigned to a business line once
      driver_business_line_unique: unique('driver_business_line_driver_business_line_unique').on(
        table.driverId,
        table.businessLineId,
      ),

      // Foreign keys
      driver_business_line_driverId_fkey: foreignKey({
        columns: [table.driverId],
        foreignColumns: [driver.id],
        name: 'driver_business_line_driverId_fkey',
      }),
      driver_business_line_businessLineId_fkey: foreignKey({
        columns: [table.businessLineId],
        foreignColumns: [business_line_config.id],
        name: 'driver_business_line_businessLineId_fkey',
      }),
    };
  },
);

/**
 * History table for driver_business_line assignments.
 * Tracks changes to driver-business line assignments over time.
 */
export const driver_business_line_history = pgTable('driver_business_line_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  driverId: uuid('driverId'),
  businessLineId: uuid('businessLineId'),
  createdBy: uuid('createdBy'),
  lastModifiedBy: uuid('lastModifiedBy'),
});

// Export Zod schemas for validation
export const DriverBusinessLineInsertSchema = createInsertSchema(driver_business_line);
export const DriverBusinessLineSelectSchema = createSelectSchema(driver_business_line);

export type SelectDriverBusinessLine = typeof driver_business_line.$inferSelect;
export type InsertDriverBusinessLine = typeof driver_business_line.$inferInsert;
